/**
 * DealWizardCard — the create-deal wizard that lives in dashboard slot E when the
 * user has no deals yet. On create it persists a real Deal (POST /deals) and the
 * deals list takes over the slot (useCreateDeal invalidates the list).
 *
 * Presentation is the founder-approved "Archetype A" two-pane design (prompt-left /
 * answer-right) ported from the mockup. The state machine, buildContext/submit
 * payload, and useCreateDeal contract are unchanged — only the render was rebuilt.
 *
 * Flow: purpose → amount + instrument-aware term → business health →
 * branch (asset-backed cost + own-contribution band / cash-flow EBITDA proxy) →
 * optional "sharpen" → name + how-soon → create.
 *
 * Inform-not-advise: no lender is named or ranked. Industry is resolved server-side
 * from the account, so it is intentionally not asked here.
 */

import {
    useEffect,
    useReducer,
    useRef,
    useState,
    type ChangeEvent,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import { Edit2, Plus } from 'react-feather';
import { Button, Card, CardOverline, Textarea } from '../../../components/ui';
import { useCreateDeal, useDeals } from '../../../api/hooks';
import { useUIStore } from '../../../stores/uiStore';
import { prefersReducedMotion } from '../../../lib/animations';
import type { Deal, DealCreate, FinancingPurpose, TimelineUrgency } from '../../../api/types';
import {
    PURPOSES,
    TERMCFG,
    HEALTH,
    URGENCY,
    CONTRIB,
    EBITDA,
    DEBT_BANDS,
    REV_BANDS,
    COMPETITIVE,
    PURPOSE_BLURB,
    SECTION_LABEL,
    INFORM,
    TITLES,
    ordinalWord,
    type Instrument,
} from './dealWizard.constants';

type Step = 'purpose' | 'amount' | 'health' | 'asset' | 'ebitda' | 'optional' | 'create';

interface WizardState {
    step: Step;
    hist: Step[];
    purpose: FinancingPurpose | null;
    assetToggle: 'assets' | 'cashflow' | null;
    otherText: string;
    amount: string;
    term: string | null; // preset year, 'custom' (field open), 'unsure' (bank decides), or null
    termCustom: string;
    health: 'healthy' | 'steady' | 'tight' | 'unsure' | null;
    totalCost: string;
    contribution: string | null;
    ebitda: string | null; // band label or 'unsure'
    debt: string | null;
    revenue: string | null;
    comp: string | null;
    dealName: string;
    dealNameEdited: boolean;
    urgency: TimelineUrgency | null;
}

/** Optional seed values — a name typed in the deal combobox, or fields the chat
 *  model extracted from the conversation. Absent keys leave the field blank. */
export interface WizardPrefill {
    purpose?: FinancingPurpose;
    amount?: number;
    urgency?: TimelineUrgency;
    dealName?: string;
}

function initialState(prefill?: WizardPrefill): WizardState {
    return {
        step: 'purpose',
        hist: [],
        purpose: prefill?.purpose ?? null,
        assetToggle: null,
        otherText: '',
        // group thousands with '.' inline (Belgian) — mirrors group() but avoids a
        // use-before-define, since group is declared further down the module.
        amount:
            prefill?.amount != null
                ? String(prefill.amount).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                : '',
        term: null,
        termCustom: '',
        health: null,
        totalCost: '',
        contribution: null,
        ebitda: null,
        debt: null,
        revenue: null,
        comp: null,
        dealName: prefill?.dealName ?? '',
        dealNameEdited: !!prefill?.dealName,
        urgency: prefill?.urgency ?? null,
    };
}

type Action =
    | { type: 'patch'; patch: Partial<WizardState> }
    | { type: 'go'; step: Step }
    | { type: 'back' }
    | { type: 'reset' };

function reducer(s: WizardState, a: Action): WizardState {
    switch (a.type) {
        case 'patch':
            return { ...s, ...a.patch };
        case 'go':
            return { ...s, step: a.step, hist: [...s.hist, s.step] };
        case 'back': {
            if (s.hist.length === 0) {
                return s;
            }
            const hist = s.hist.slice();
            const step = hist.pop() as Step;
            return { ...s, step, hist };
        }
        case 'reset':
            return initialState();
        default:
            return s;
    }
}

// ── pure helpers (contract — unchanged) ─────────────────────────────────────
const onlyDigits = (s: string) => (s || '').replace(/[^0-9]/g, '');
const num = (s: string) => {
    const d = onlyDigits(s);
    return d ? parseInt(d, 10) : 0;
};
const group = (s: string) => onlyDigits(s).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const dec1 = (x: number) => (Math.round(x * 10) / 10).toString().replace('.', ',');
const shortAmt = (n: number) =>
    n >= 1_000_000 ? `${dec1(n / 1_000_000)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);

function instrument(s: WizardState): Instrument | 'working_capital' {
    if (s.purpose === 'acquisition') {
        return s.assetToggle === 'assets' ? 'acquisition_asset' : 'acquisition';
    }
    return (s.purpose ?? 'other') as Instrument;
}
const assetBacked = (s: WizardState) => {
    const i = instrument(s);
    return i === 'real_estate' || i === 'equipment' || i === 'acquisition_asset';
};
const decodeKey = (s: WizardState): FinancingPurpose => s.purpose ?? 'other';
const termCfg = (s: WizardState) => {
    const i = instrument(s);
    return i === 'working_capital' ? null : (TERMCFG[i] ?? TERMCFG.other);
};

function autoName(s: WizardState): string {
    const label = PURPOSES.find(p => p.k === decodeKey(s))?.label ?? 'Deal';
    const n = num(s.amount);
    const a = n > 0 ? ` · €${shortAmt(n)}` : '';
    let t = '';
    if (s.term === 'custom' && s.termCustom) {
        t = ` · ${s.termCustom} yr`;
    } else if (s.term && s.term !== 'custom' && s.term !== 'unsure' && termCfg(s)) {
        t = ` · ${s.term} yr`;
    }
    return label + a + t;
}

function canNext(s: WizardState): boolean {
    switch (s.step) {
        case 'purpose':
            return (
                !!s.purpose &&
                (s.purpose !== 'acquisition' || !!s.assetToggle) &&
                (s.purpose !== 'other' || !!s.otherText.trim())
            );
        case 'amount': {
            const cfg = termCfg(s);
            return num(s.amount) > 0 && (cfg === null || s.term != null);
        }
        case 'health':
            return !!s.health;
        case 'asset':
            return num(s.totalCost) > 0 && !!s.contribution;
        case 'ebitda':
            return true;
        case 'optional':
            return true;
        case 'create':
            return !!s.dealName.trim() && !!s.urgency;
        default:
            return false;
    }
}

function nextStep(s: WizardState): Step {
    switch (s.step) {
        case 'purpose':
            return 'amount';
        case 'amount':
            return 'health';
        case 'health':
            return assetBacked(s) ? 'asset' : s.health === 'healthy' ? 'ebitda' : 'optional';
        case 'asset':
            return 'optional';
        case 'ebitda':
            return 'optional';
        case 'optional':
            return 'create';
        default:
            return 'create';
    }
}

function pathSteps(s: WizardState): Step[] {
    const steps: Step[] = ['purpose', 'amount', 'health'];
    if (assetBacked(s)) {
        steps.push('asset');
    } else if (s.health === 'healthy') {
        steps.push('ebitda');
    }
    steps.push('optional', 'create');
    return steps;
}

// Exact strings matched here MUST stay in lockstep with COMPETITIVE (constants).
function compToBool(comp: string | null): boolean | undefined {
    if (comp === 'Only one bank') {
        return false;
    }
    if (comp === '2+ banks' || comp === 'Broker or RFP') {
        return true;
    }
    return undefined; // "Not sure" / unset
}

function buildContext(s: WizardState): Record<string, unknown> | undefined {
    const raw: Record<string, unknown> = {
        business_health: s.health ?? undefined,
        existing_debt_band: s.debt ?? undefined,
        revenue_band: s.revenue ?? undefined,
        // Free-text description for the "Other" purpose — persisted so we can see
        // what users actually want financed and feed it to the LLM as context.
        other_description: s.purpose === 'other' ? s.otherText.trim() : undefined,
    };
    if (s.term === 'custom' && s.termCustom) {
        raw.requested_tenor_years = Number(s.termCustom);
    } else if (s.term && s.term !== 'custom' && s.term !== 'unsure' && termCfg(s)) {
        raw.requested_tenor_years = Number(s.term);
    }
    if (assetBacked(s)) {
        if (num(s.totalCost) > 0) {
            raw.total_cost = num(s.totalCost);
        }
        if (s.contribution) {
            raw.own_contribution_band = s.contribution;
        }
    } else if (s.ebitda && s.ebitda !== 'unsure') {
        raw.ebitda_band = s.ebitda;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) {
        if (v !== undefined && v !== null && v !== '') {
            out[k] = v;
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
}

// ── view helpers (left-pane "deal so far" receipt) ──────────────────────────
const purposeLabel = (s: WizardState) => PURPOSES.find(p => p.k === decodeKey(s))?.label ?? 'Deal';
const healthShort = (s: WizardState) =>
    s.health === 'healthy'
        ? 'Healthy'
        : s.health === 'steady'
          ? 'Steady'
          : s.health === 'tight'
            ? 'Tight'
            : s.health === 'unsure'
              ? 'Not sure'
              : '';
function termText(s: WizardState): string {
    if (termCfg(s) === null) {
        return '';
    } // working capital — no term in the receipt
    if (s.term === 'custom') {
        return s.termCustom ? `${s.termCustom} yr` : '';
    }
    if (s.term === 'unsure') {
        return 'term open';
    }
    return s.term ? `${s.term} yr` : '';
}
function sofarParts(s: WizardState): string[] {
    const parts: string[] = [];
    if (s.purpose) {
        parts.push(purposeLabel(s));
    }
    if (num(s.amount) > 0) {
        parts.push(`€${shortAmt(num(s.amount))}`);
    }
    const t = termText(s);
    if (t) {
        parts.push(t);
    }
    if (s.health) {
        parts.push(healthShort(s));
    }
    return parts;
}

// ── small presentational helpers ────────────────────────────────────────────
function Chip({
    selected,
    onClick,
    small,
    children,
}: {
    selected: boolean;
    onClick: () => void;
    small?: boolean;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={clsx(
                'border transition-colors select-none text-left leading-snug',
                small ? 'text-body-sm px-3 py-1.5' : 'text-body px-4 py-2',
                selected
                    ? 'border-orange text-orange bg-orange/10'
                    : 'border-border text-txt-secondary bg-surface-card hover:border-border-strong hover:text-txt-primary hover:bg-surface-subtle'
            )}
        >
            {children}
        </button>
    );
}

function AmountField({
    value,
    onChange,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
}) {
    const ref = useRef<HTMLInputElement>(null);
    const handle = (e: ChangeEvent<HTMLInputElement>) => {
        const rawStr = e.target.value;
        const caret = e.target.selectionStart ?? rawStr.length;
        const digitsLeft = (rawStr.slice(0, caret).match(/\d/g) || []).length;
        const formatted = group(rawStr);
        onChange(formatted);
        requestAnimationFrame(() => {
            const el = ref.current;
            if (!el) {
                return;
            }
            let pos = formatted.length;
            if (digitsLeft > 0) {
                let seen = 0;
                for (pos = 0; pos < formatted.length; pos++) {
                    if (/\d/.test(formatted[pos])) {
                        seen++;
                        if (seen >= digitsLeft) {
                            pos++;
                            break;
                        }
                    }
                }
            } else {
                pos = 0;
            }
            try {
                el.setSelectionRange(pos, pos);
            } catch {
                /* noop */
            }
        });
    };
    return (
        <div className="flex items-stretch border border-border bg-surface-subtle focus-within:border-orange">
            <span className="px-3 py-2 font-mono text-body-sm text-txt-tertiary border-r border-border flex items-center">
                EUR
            </span>
            <input
                ref={ref}
                inputMode="numeric"
                value={value}
                placeholder={placeholder}
                onChange={handle}
                className="flex-1 min-w-0 bg-transparent outline-none px-3 py-2 font-mono text-body text-txt-primary placeholder:text-txt-tertiary"
            />
        </div>
    );
}

const flab = 'text-overline uppercase text-txt-secondary mb-1.5';

/**
 * Instrument-aware term control: preset year chips + "Not sure" + a persistent
 * +/× that morphs the presets into a custom-years input. Reuses the app's proven
 * max-width/flex-basis morph (no clip-wipe @property) restyled to the mockup —
 * a single crisp orange frame on focus, no doubled seam.
 */
function TermControl({
    s,
    patch,
    reduce,
    T,
}: {
    s: WizardState;
    patch: (p: Partial<WizardState>) => void;
    reduce: boolean;
    T: number;
}) {
    const cfg = termCfg(s);
    const customRef = useRef<HTMLInputElement>(null);
    const expanded = s.term === 'custom';
    useEffect(() => {
        if (expanded) {
            const t = setTimeout(() => customRef.current?.focus(), reduce ? 0 : T);
            return () => clearTimeout(t);
        }
    }, [expanded, reduce, T]);

    // Working capital has no term — show nothing (never explain an absence).
    if (cfg === null) {
        return null;
    }

    const n = cfg.chips.length;
    const y = parseInt(onlyDigits(s.termCustom), 10);
    const note =
        expanded && y > cfg.max
            ? `Banks usually structure this type up to about ${cfg.max} years. Above that is unusual, and the bank may not offer it.`
            : s.term === 'unsure'
              ? 'The term stays open. The bank proposes one in its offer.'
              : '';
    const toggle = () =>
        patch(
            s.term === 'custom'
                ? { term: null, termCustom: '' }
                : { term: 'custom', termCustom: '' }
        );

    return (
        <div>
            <p className={flab}>
                Over how long? · years <span className="text-orange">•</span>
            </p>
            <div className="flex flex-nowrap items-stretch h-9">
                {cfg.chips.map((t, i) => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => patch({ term: s.term === t ? null : t, termCustom: '' })}
                        aria-pressed={s.term === t}
                        className={clsx(
                            'inline-flex items-center justify-center font-mono text-body-sm border overflow-hidden whitespace-nowrap tabular-nums',
                            s.term === t
                                ? 'border-orange text-orange bg-orange/10'
                                : 'border-border text-txt-secondary hover:border-border-strong hover:text-txt-primary'
                        )}
                        style={{
                            maxWidth: expanded ? 0 : 60,
                            opacity: expanded ? 0 : 1,
                            marginRight: expanded ? 0 : 6,
                            paddingLeft: expanded ? 0 : 10,
                            paddingRight: expanded ? 0 : 10,
                            pointerEvents: expanded ? 'none' : undefined,
                            transition: reduce
                                ? 'none'
                                : `max-width ${T}ms var(--ease-std), opacity ${Math.round(T * 0.7)}ms ease, margin ${T}ms var(--ease-std), padding ${T}ms var(--ease-std), border-color 200ms ease`,
                            transitionDelay: reduce ? '0ms' : `${(n - 1 - i) * 16}ms`,
                        }}
                    >
                        {t}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() =>
                        patch({ term: s.term === 'unsure' ? null : 'unsure', termCustom: '' })
                    }
                    aria-pressed={s.term === 'unsure'}
                    className={clsx(
                        'inline-flex items-center justify-center text-body-sm border overflow-hidden whitespace-nowrap',
                        s.term === 'unsure'
                            ? 'border-orange text-orange bg-orange/10'
                            : 'border-border text-txt-secondary hover:border-border-strong hover:text-txt-primary'
                    )}
                    style={{
                        maxWidth: expanded ? 0 : 90,
                        opacity: expanded ? 0 : 1,
                        marginRight: expanded ? 0 : 6,
                        paddingLeft: expanded ? 0 : 10,
                        paddingRight: expanded ? 0 : 10,
                        pointerEvents: expanded ? 'none' : undefined,
                        transition: reduce
                            ? 'none'
                            : `max-width ${T}ms var(--ease-std), opacity ${Math.round(T * 0.7)}ms ease, margin ${T}ms var(--ease-std), padding ${T}ms var(--ease-std), border-color 200ms ease`,
                    }}
                >
                    Not sure
                </button>
                <div
                    className={clsx(
                        'relative flex items-center h-9 border bg-surface-subtle overflow-hidden',
                        expanded ? 'border-orange' : 'border-border'
                    )}
                    style={{
                        flexGrow: 0,
                        flexShrink: 0,
                        flexBasis: expanded ? 200 : 36,
                        transition: reduce
                            ? 'none'
                            : `flex-basis ${T}ms var(--ease-std), border-color 200ms ease`,
                    }}
                >
                    <input
                        ref={customRef}
                        inputMode="numeric"
                        value={s.termCustom}
                        placeholder={cfg.anchor}
                        onChange={e => patch({ termCustom: onlyDigits(e.target.value) })}
                        className="flex-1 min-w-0 bg-transparent outline-none px-3 font-mono text-body text-txt-primary placeholder:text-txt-tertiary focus:outline-none"
                        style={{
                            opacity: expanded ? 1 : 0,
                            pointerEvents: expanded ? 'auto' : 'none',
                            transition: reduce ? 'none' : 'opacity 200ms ease 40ms',
                        }}
                        aria-hidden={!expanded}
                        aria-label="Custom term in years"
                        tabIndex={expanded ? 0 : -1}
                    />
                    <span
                        className="pr-10 font-mono text-caption text-txt-tertiary"
                        style={{
                            opacity: expanded ? 1 : 0,
                            transition: reduce ? 'none' : 'opacity 200ms ease 40ms',
                        }}
                    >
                        years
                    </span>
                    <button
                        type="button"
                        onClick={toggle}
                        aria-label={expanded ? 'Use the preset terms' : 'Enter a custom term'}
                        className={clsx(
                            'absolute top-0 bottom-0 flex items-center justify-center w-9 z-10 border-l',
                            expanded
                                ? 'text-orange border-orange'
                                : 'text-txt-secondary border-transparent hover:text-txt-primary'
                        )}
                        style={{
                            left: expanded ? 'calc(100% - 36px)' : 0,
                            transition: reduce
                                ? 'none'
                                : `left ${T}ms var(--ease-std), color 200ms ease`,
                        }}
                    >
                        <Plus
                            size={16}
                            style={{
                                transform: expanded ? 'rotate(45deg)' : 'none',
                                transition: reduce ? 'none' : `transform ${T}ms var(--ease-std)`,
                            }}
                        />
                    </button>
                </div>
            </div>
            <p className="text-caption text-txt-tertiary mt-2 leading-snug min-h-[2.2em] max-w-[46ch]">
                {note}
            </p>
        </div>
    );
}

/** Five/six-square stepper + a stage word that never shifts (CSS grid-stack of
 *  every label sizes the box to the widest; only the active one is visible). */
function Stepper({ s }: { s: WizardState }) {
    const ps = pathSteps(s);
    const ci = ps.indexOf(s.step);
    const labels = Object.values(SECTION_LABEL);
    return (
        <div
            className="flex items-center gap-3"
            role="progressbar"
            aria-valuenow={ci + 1}
            aria-valuemin={1}
            aria-valuemax={ps.length}
            aria-label="Deal setup progress"
        >
            <div className="flex items-center gap-1.5">
                {ps.map((st, i) => (
                    <span
                        key={st}
                        className={clsx(
                            'w-[7px] h-[7px] border',
                            i <= ci ? 'bg-orange border-orange' : 'border-border-strong'
                        )}
                        style={{
                            transition:
                                'background-color var(--motion-fade) var(--ease-std), border-color var(--motion-fade) var(--ease-std)',
                        }}
                    />
                ))}
            </div>
            <span className="grid justify-items-end">
                {labels.map(l => (
                    <span
                        key={l}
                        className={clsx(
                            'col-start-1 row-start-1 text-overline uppercase tracking-wide',
                            l === SECTION_LABEL[s.step] ? 'text-txt-secondary' : 'invisible'
                        )}
                    >
                        {l}
                    </span>
                ))}
            </span>
        </div>
    );
}

export function DealWizardCard({
    onCreated,
    variant = 'slot',
    prefill,
    onCancel,
}: {
    onCreated?: (deal: Deal, sourceRect?: DOMRect) => void;
    variant?: 'slot' | 'modal' | 'chat';
    prefill?: WizardPrefill;
    onCancel?: () => void;
}) {
    const [s, dispatch] = useReducer(reducer, prefill, initialState);
    const createDeal = useCreateDeal();
    const { showToast } = useUIStore();
    // Welcome-line ordinal: the deal being created is the (existing deals + 1)-th.
    // useDeals is cached — same query key the dashboard/deals list already populated.
    const existingDeals = useDeals().data?.deals ?? [];
    const ordinal = ordinalWord(existingDeals.filter((d) => !d.is_archived).length + 1);
    const patch = (p: Partial<WizardState>) => dispatch({ type: 'patch', patch: p });
    const reduce = prefersReducedMotion();
    const T = reduce ? 0 : 240;
    const [hoverBlurb, setHoverBlurb] = useState<string | null>(null);

    const submit = async () => {
        const context = buildContext(s);
        const payload: DealCreate = {
            financing_purpose: decodeKey(s),
            financing_amount: num(s.amount),
            timeline_urgency: s.urgency as TimelineUrgency,
            deal_name: s.dealName.trim() || undefined,
            currency: 'EUR',
            ...(compToBool(s.comp) !== undefined
                ? { is_competitive_process: compToBool(s.comp) }
                : {}),
            ...(context ? { deal_context: context } : {}),
            // industry intentionally omitted — resolved from the account server-side
        };
        try {
            const created = await createDeal.mutateAsync(payload);
            showToast('Deal created.', 'success');
            onCreated?.(created);
            // The deals-list card takes over slot E once the invalidated list refetches.
        } catch (err) {
            const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
                ?.detail;
            const msg =
                typeof detail === 'string' ? detail : 'Could not create the deal. Try again.';
            showToast(msg, 'error');
        }
    };

    const onCreate = s.step === 'create';
    const nextEnabled = canNext(s);
    const advance = () => {
        const nx = nextStep(s);
        if (nx === 'create' && !s.dealNameEdited) {
            dispatch({ type: 'patch', patch: { dealName: autoName(s) } });
        }
        dispatch({ type: 'go', step: nx });
    };

    // Focus the step's primary input on each step change (not on first mount, so the
    // dashboard doesn't yank focus into the card on load). Amount steps land the caret
    // in their field so the user can just type; chip-only steps focus the region.
    const bodyRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const nameRef = useRef<HTMLInputElement>(null);
    const firstRun = useRef(true);
    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false;
            return;
        }
        if (s.step === 'create') {
            nameRef.current?.focus();
            nameRef.current?.select();
            return;
        }
        const firstInput = stageRef.current?.querySelector('input');
        if (firstInput) {
            firstInput.focus({ preventScroll: true });
        } else {
            bodyRef.current?.focus();
        }
    }, [s.step]);

    const stepEnter = reduce ? undefined : 'chatCardEnter var(--motion-step) var(--ease-out) both';
    const onBodyKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Enter' || (e.target as HTMLElement).tagName !== 'INPUT') {
            return;
        }
        if (!nextEnabled || createDeal.isPending) {
            return;
        }
        if (onCreate || s.step === 'amount') {
            e.preventDefault();
            if (onCreate) {
                void submit();
            } else {
                advance();
            }
        }
    };

    // ── left pane (prompt) ────────────────────────────────────────────────────
    const wcTitle = s.step === 'amount' && termCfg(s) === null;
    const titleKey = wcTitle ? 'amount_wc' : s.step;
    const renderPrompt = () => {
        if (s.step === 'purpose') {
            const blurb = hoverBlurb ?? (s.purpose ? PURPOSE_BLURB[s.purpose] : null);
            return (
                <div className="relative flex-1 flex flex-col justify-center min-h-[3.5rem]">
                    <p
                        className="text-h2 font-bold text-txt-primary leading-tight max-w-[15ch]"
                        style={{
                            opacity: blurb ? 0 : 1,
                            transition: reduce
                                ? 'none'
                                : 'opacity var(--motion-fade) var(--ease-std)',
                        }}
                    >
                        Let’s set up your {ordinal} <span className="text-orange">deal</span>
                    </p>
                    <p
                        className="dw-inform absolute inset-0 flex items-center text-body-sm text-txt-secondary max-w-[42ch]"
                        style={{
                            opacity: blurb ? 1 : 0,
                            transition: reduce
                                ? 'none'
                                : 'opacity var(--motion-fade) var(--ease-std)',
                        }}
                        aria-hidden={!blurb}
                    >
                        {blurb ?? `Let’s set up your ${ordinal} deal`}
                    </p>
                </div>
            );
        }
        return (
            <>
                <div className="flex-1 flex flex-col justify-center">
                    <h2 className="text-h2 font-bold text-txt-primary leading-tight">
                        {TITLES[titleKey]}
                    </h2>
                    <p className="dw-inform text-body-sm text-txt-secondary mt-3 max-w-[46ch] border-l-2 border-border pl-3">
                        {INFORM[titleKey]}
                    </p>
                </div>
                {sofarParts(s).length > 0 && (
                    <div className="dw-sofar mt-auto pt-4">
                        <p className="text-caption uppercase tracking-wide text-txt-tertiary mb-1">
                            Deal so far
                        </p>
                        <p className="font-mono text-body-sm text-txt-secondary">
                            {sofarParts(s).join(' · ')}
                        </p>
                    </div>
                )}
            </>
        );
    };

    // ── right pane (answer stage) ─────────────────────────────────────────────
    const rq = 'text-body font-semibold text-txt-primary mb-2 leading-snug';
    const renderAnswer = () => {
        switch (s.step) {
            case 'purpose':
                return (
                    <>
                        <p className={rq}>What are you looking to finance?</p>
                        <div className="flex flex-wrap gap-2">
                            {PURPOSES.map(p => (
                                <span
                                    key={p.k}
                                    onMouseEnter={() => setHoverBlurb(PURPOSE_BLURB[p.k])}
                                    onMouseLeave={() => setHoverBlurb(null)}
                                    onFocus={() => setHoverBlurb(PURPOSE_BLURB[p.k])}
                                    onBlur={() => setHoverBlurb(null)}
                                    className="inline-flex"
                                >
                                    <Chip
                                        selected={s.purpose === p.k}
                                        onClick={() =>
                                            patch({
                                                purpose: s.purpose === p.k ? null : p.k,
                                                assetToggle: null,
                                                otherText: '',
                                                term: null,
                                                termCustom: '',
                                            })
                                        }
                                    >
                                        {p.label}
                                    </Chip>
                                </span>
                            ))}
                        </div>
                        {s.purpose === 'acquisition' && (
                            <div className="mt-3" style={{ animation: stepEnter }}>
                                <p className={flab}>Buying its assets, or its shares?</p>
                                <div className="flex flex-wrap gap-2">
                                    <Chip
                                        selected={s.assetToggle === 'assets'}
                                        onClick={() =>
                                            patch({
                                                assetToggle:
                                                    s.assetToggle === 'assets' ? null : 'assets',
                                                term: null,
                                                termCustom: '',
                                            })
                                        }
                                    >
                                        Mostly assets
                                    </Chip>
                                    <Chip
                                        selected={s.assetToggle === 'cashflow'}
                                        onClick={() =>
                                            patch({
                                                assetToggle:
                                                    s.assetToggle === 'cashflow'
                                                        ? null
                                                        : 'cashflow',
                                                term: null,
                                                termCustom: '',
                                            })
                                        }
                                    >
                                        Mostly shares
                                    </Chip>
                                </div>
                            </div>
                        )}
                        {s.purpose === 'other' && (
                            <div className="mt-2 max-w-[360px]" style={{ animation: stepEnter }}>
                                <p className={flab}>In your own words</p>
                                <Textarea
                                    rows={2}
                                    resize="none"
                                    value={s.otherText}
                                    onChange={e => patch({ otherText: e.target.value })}
                                    placeholder="e.g. a co-founder buyout, or franchise fees"
                                />
                            </div>
                        )}
                    </>
                );
            case 'amount':
                return (
                    <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
                        <div>
                            <p className={flab}>
                                {termCfg(s) === null ? 'Credit line' : 'Amount to borrow'}{' '}
                                <span className="text-orange">•</span>
                            </p>
                            <div className="w-[150px] max-w-full">
                                <AmountField
                                    value={s.amount}
                                    onChange={v => patch({ amount: v })}
                                    placeholder="800.000"
                                />
                            </div>
                        </div>
                        <div className="min-w-0">
                            <TermControl s={s} patch={patch} reduce={reduce} T={T} />
                        </div>
                    </div>
                );
            case 'health':
                return (
                    <div className="max-w-[420px]">
                        <p className={flab}>
                            The general picture <span className="text-orange">•</span>
                        </p>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(176px,1fr))] gap-2">
                            {HEALTH.map(h => (
                                <Chip
                                    key={h.k}
                                    selected={s.health === h.k}
                                    onClick={() => patch({ health: s.health === h.k ? null : h.k })}
                                >
                                    {h.label}
                                </Chip>
                            ))}
                        </div>
                    </div>
                );
            case 'asset':
                return (
                    <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
                        <div>
                            <p className={flab}>
                                Total cost <span className="text-orange">•</span>
                            </p>
                            <div className="w-[150px] max-w-full">
                                <AmountField
                                    value={s.totalCost}
                                    onChange={v => patch({ totalCost: v })}
                                    placeholder="1.200.000"
                                />
                            </div>
                        </div>
                        <div>
                            <p className={flab}>
                                Your own contribution <span className="text-orange">•</span>
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {CONTRIB.map(x => (
                                    <Chip
                                        key={x.l}
                                        small
                                        selected={s.contribution === x.l}
                                        onClick={() =>
                                            patch({
                                                contribution: s.contribution === x.l ? null : x.l,
                                            })
                                        }
                                    >
                                        {x.l}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'ebitda':
                return (
                    <div>
                        <p className="text-caption text-txt-tertiary mb-2 border-l-2 border-orange pl-2.5 leading-snug max-w-[44ch]">
                            Not your turnover. What’s left after the day-to-day running costs
                            (roughly your EBITDA).
                        </p>
                        <p className={flab}>In a normal year</p>
                        <div className="flex flex-wrap gap-2">
                            {EBITDA.map(b => (
                                <Chip
                                    key={b.l}
                                    small
                                    selected={s.ebitda === b.l}
                                    onClick={() => patch({ ebitda: s.ebitda === b.l ? null : b.l })}
                                >
                                    {b.l}
                                </Chip>
                            ))}
                            <Chip
                                small
                                selected={s.ebitda === 'unsure'}
                                onClick={() =>
                                    patch({ ebitda: s.ebitda === 'unsure' ? null : 'unsure' })
                                }
                            >
                                Not sure, I’d need to check
                            </Chip>
                        </div>
                    </div>
                );
            case 'optional':
                // Two columns across (landscape). Left column STACKS Debt + Lenders so the
                // Lenders question lifts up under Debt (filling the gap left by the taller
                // Revenue column) instead of dropping to a second row. Debt (top-left) and
                // Revenue (top-right) stay put.
                return (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <div className="flex flex-col gap-4">
                            <div>
                                <p className={flab}>Existing bank debt?</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {DEBT_BANDS.map(d => (
                                        <Chip
                                            key={d}
                                            small
                                            selected={s.debt === d}
                                            onClick={() => patch({ debt: s.debt === d ? null : d })}
                                        >
                                            {d}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className={flab}>Talking to more than one lender?</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {COMPETITIVE.map(c => (
                                        <Chip
                                            key={c}
                                            small
                                            selected={s.comp === c}
                                            onClick={() => patch({ comp: s.comp === c ? null : c })}
                                        >
                                            {c}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div>
                            <p className={flab}>Company revenue?</p>
                            <div className="flex flex-wrap gap-1.5">
                                {REV_BANDS.map(r => (
                                    <Chip
                                        key={r}
                                        small
                                        selected={s.revenue === r}
                                        onClick={() =>
                                            patch({ revenue: s.revenue === r ? null : r })
                                        }
                                    >
                                        {r}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            case 'create':
                return (
                    <div className="max-w-[380px]">
                        <p className={flab}>
                            Name your deal <span className="text-orange">•</span>
                        </p>
                        <div className="flex items-stretch border border-border bg-surface-subtle focus-within:border-orange">
                            <input
                                ref={nameRef}
                                type="text"
                                value={s.dealName}
                                placeholder="Name this deal"
                                onChange={e =>
                                    patch({ dealName: e.target.value, dealNameEdited: true })
                                }
                                className="flex-1 min-w-0 bg-transparent outline-none px-3 py-2 text-body text-txt-primary placeholder:text-txt-tertiary"
                            />
                            <Edit2
                                size={13}
                                className="self-center mr-3 flex-shrink-0 text-txt-tertiary"
                                aria-hidden
                            />
                        </div>
                        <div className="mt-3.5">
                            <p className={flab}>How soon do you need it?</p>
                            <div className="flex flex-wrap gap-2">
                                {URGENCY.map(u => (
                                    <Chip
                                        key={u.k}
                                        small
                                        selected={s.urgency === u.k}
                                        onClick={() =>
                                            patch({ urgency: s.urgency === u.k ? null : u.k })
                                        }
                                    >
                                        {u.label}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const inner = (
        <div
            ref={bodyRef}
            tabIndex={-1}
            aria-live="polite"
            onKeyDown={onBodyKeyDown}
            className="flex-1 min-h-0 overflow-hidden outline-none"
        >
            {/* Two self-contained panes — no full-width strips. The prompt pane owns
            the overline + title/inform + "deal so far"; the answer pane owns the
            stepper (top), the controls (middle), and Skip/Back/Next (bottom). Each
            pane is a flex column, so its top and bottom rows land at the same spot
            on every step regardless of content height (the "frame never moves"). */}
            <div
                key={s.step}
                style={{ animation: stepEnter }}
                className="h-full grid grid-cols-1 md:grid-cols-[38%_minmax(0,1fr)]"
            >
                <div className="flex flex-col min-h-0 min-w-0 px-5 py-4 md:border-r md:border-border">
                    {s.step !== 'purpose' && (
                        <div className="mb-3">
                            <CardOverline>Create your {ordinal} deal</CardOverline>
                        </div>
                    )}
                    {renderPrompt()}
                </div>
                <div ref={stageRef} className="flex flex-col min-h-0 min-w-0 px-5 py-4">
                    <div className="flex justify-end mb-4">
                        <Stepper s={s} />
                    </div>
                    {/* Top-anchored (not centered) so a tall step grows DOWNWARD and never
                overflows up into the stepper; overflow-hidden keeps it out of the nav. */}
                    <div className="flex-1 flex flex-col justify-start min-h-0 overflow-hidden">
                        {renderAnswer()}
                    </div>
                    <div
                        className={clsx(
                            'flex items-center gap-3',
                            variant === 'chat' && onCancel ? 'justify-between' : 'justify-end'
                        )}
                    >
                        {variant === 'chat' && onCancel && (
                            <Button
                                variant="tertiary"
                                size="sm"
                                type="button"
                                onClick={onCancel}
                                className="font-normal"
                            >
                                Cancel
                            </Button>
                        )}
                        <div className="flex items-center gap-3">
                            <Button
                                variant="tertiary"
                                size="sm"
                                onClick={() => dispatch({ type: 'back' })}
                                className={clsx('font-normal', s.hist.length === 0 && 'invisible')}
                            >
                                Back
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                type="button"
                                disabled={!nextEnabled || createDeal.isPending}
                                isLoading={onCreate && createDeal.isPending}
                                onClick={() => (onCreate ? submit() : advance())}
                            >
                                {onCreate ? 'Create deal' : 'Next'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Same inner tree, three containers. Slot = the fluid dashboard cell (unchanged
    // default). Modal = a fixed comfortable height inside the Modal card. Chat =
    // a bordered card that sizes to content in the scrolling conversation.
    if (variant === 'modal') {
        return (
            // Landscape frame the two-pane was designed for (~800×267 mockup):
            // 280px when the panes sit side by side (md+), taller when they stack.
            <div className="flex flex-col overflow-hidden h-[440px] md:h-[280px]">{inner}</div>
        );
    }
    if (variant === 'chat') {
        return (
            <div className="flex flex-col overflow-hidden border border-border bg-surface-card min-h-[420px]">
                {inner}
            </div>
        );
    }
    return (
        <Card padding="none" className="h-full flex flex-col overflow-hidden">
            {inner}
        </Card>
    );
}
